import {
  GDTSettingsDto,
  GDTSettingsPatchDto,
  QueryFactory,
  TransportProtocol,
} from '@lmt/shared-types';
import { VisitType } from '@lmt/unicorn-shared/app/components/calendar/visit/common/types';
import { FieldBlock } from '@lmt/unicorn-shared/app/components/common/FieldBlock';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { ToggleButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  maxRule,
  minRule,
  positiveRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field } from '../../common/Field';
import { CheckBox } from '../../common/ui-kit/CheckBox';
import { DropDownInput } from '../../common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '../../common/ui-kit/inputs/Input';
import { TransporterState } from '../../gdt/messages/components/TransporterState';
import { characterEncodings } from './hl7/common';

import styles from './SettingsBlock.module.scss';
import { TenantSettingsKeys } from './types';
import { useRegisterUnsavedChanges } from './useRegisterUnsavedChanges';
import { useEventCallback } from '@mui/material';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import { Loading } from '../../common/Loading';

type GdtSettingsData = Omit<
  GDTSettingsDto,
  'onlyProcessMessagesForExistingPatients'
> & {
  visitLengthInHours: number;
  gdtOnlyProcessMessagesForExistingPatients: boolean;
};

const defaultSettingsData: GdtSettingsData = {
  appName: '',
  caseSearchBackwardDaysCount: 0,
  caseSearchForwardDaysCount: 0,
  defaultVisitTypeId: 0,
  encoding: '',
  subtenantId: 0,
  visitLengthInHours: 0,
  visitLengthInSeconds: 0,
  enabled: false,
  gdtOnlyProcessMessagesForExistingPatients: false,
  cleanupEnabled: false,
  deleteAfterMonths: 6,
  isCleanupJobEnabledGlobally: false,
};

export const GdtSettings: FC = React.memo(function GdtSettings() {
  const subtenants = useSubtenants();
  const i18next = useScopedTranslation('tenant_settings.gdt');
  const i18nextGlobal = useTranslation();
  const claims = useClaims();
  const modals = useModal();

  const {
    data: gdtSettings,
    refetch: refetch,
    isLoading: isGdtSettingsLoading,
  } = QueryFactory.GDTSettingsQuery.useGetQuery({
    enabled: claims.permissions.gdtManagement,
  });

  const settingsData: GdtSettingsData = useMemo(() => {
    if (!gdtSettings) {
      return defaultSettingsData;
    }
    return {
      ...gdtSettings,
      visitLengthInHours: gdtSettings.visitLengthInSeconds / 3600,
      gdtOnlyProcessMessagesForExistingPatients:
        gdtSettings.onlyProcessMessagesForExistingPatients,
    };
  }, [gdtSettings]);

  const form = useAdvancedForm<GdtSettingsData>(
    useCallback(async (data: GdtSettingsData) => {
      try {
        const dto: GDTSettingsPatchDto = {
          ...data,
          visitLengthInSeconds: data.visitLengthInHours * 3600,
          onlyProcessMessagesForExistingPatients:
            data.gdtOnlyProcessMessagesForExistingPatients,
        };
        await QueryFactory.GDTSettingsClient.patch(dto);
        await refetch();
      } catch (error) {
        const errorDetails = handleSubmitFormError(error, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }, []),
    settingsData,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );
  const { formState, reset, register, control, setError, setValue, watch } =
    form;

  const [cleanupEnabled] = watch(['cleanupEnabled']);

  const onReset = useCallback(() => {
    reset(settingsData);
  }, [settingsData]);

  const visitTypes = useActiveVisitTypes();
  const translatedVisitTypes: VisitType[] = useMemo(
    () =>
      visitTypes.map((type) => ({
        id: type.id,
        label: i18nextGlobal.t(type.value as any),
        defaultDurationInSeconds: type.defaultDurationInSeconds,
      })),
    [visitTypes],
  );

  useEffect(() => {
    onReset();
  }, [settingsData]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.GDT,
    form,
  );

  const setVisitLength = useEventCallback((visitType: VisitType) => {
    if (visitType?.defaultDurationInSeconds) {
      setValue(
        'visitLengthInHours',
        visitType.defaultDurationInSeconds / 3600,
        validateAndDirty,
      );
    }
  });

  return (
    <Loading loading={isGdtSettingsLoading}>
      <form onSubmit={form.handleSubmitDefault}>
        <div className={styles.container} data-test-id="gdt-settings-container">
          <div className={styles.header}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>{i18next.t('title')}</div>
              {gdtSettings?.enabled && (
                <TransporterState protocol={TransportProtocol.GDT} />
              )}
            </div>
            <div className={styles.optionContainer}>
              <Button
                disabled={!isUnsavedChanges}
                title={i18next.t('reset_button_title')}
                className={styles.option}
                color={ButtonColor.Primary}
                onClick={onReset}
              />
              <SubmitButton
                disabled={!isUnsavedChanges}
                title={i18next.t('submit_button_title')}
                submitting={formState.isSubmitting}
                className={styles.option}
              />
            </div>
          </div>
          <div className={styles.dataContainer}>
            <div className={styles.blockContainer}>
              <FieldBlock
                title={i18next.t('general_settings')}
                className={styles.fieldBlock}
              >
                <CheckBox
                  title={i18next.t('gdt_enabled')}
                  className={styles.checkbox}
                  {...register('enabled')}
                />
                <CheckBox
                  title={i18next.t('only_process_existing_patients')}
                  {...register('gdtOnlyProcessMessagesForExistingPatients')}
                  className={styles.checkbox}
                />
                <Field
                  title={i18next.t('default_subtenant')}
                  className={styles.field}
                >
                  <Controller
                    name={'subtenantId'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <DropDownInput
                        useIdFunctionAsValue={true}
                        required
                        getOptionLabel="name"
                        idFunction="id"
                        options={subtenants}
                        value={value}
                        onValueChanged={(value) => onChange(value.id)}
                      />
                    )}
                  />
                </Field>
                <Field title={i18next.t('encoding')} className={styles.field}>
                  <Controller
                    name={'encoding'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <DropDownInput
                        options={characterEncodings}
                        required={true}
                        value={value}
                        onValueChanged={(value) => onChange(value)}
                      />
                    )}
                  />
                </Field>
                <Field title={i18next.t('app_name')} className={styles.field}>
                  <Input variant="normal" {...register('appName')} />
                </Field>
                <Field className={styles.field} title={''}>
                  <ToggleButton
                    {...register('cleanupEnabled')}
                    title={i18next.t('cleanup_enabled')}
                    disabled={!gdtSettings?.isCleanupJobEnabledGlobally}
                    hint={
                      gdtSettings?.isCleanupJobEnabledGlobally
                        ? null
                        : i18next.t('cleanup_disabled_globally_hint')
                    }
                  />
                </Field>
                <Field
                  title={i18next.t('delete_after_months')}
                  className={styles.field}
                >
                  <Input
                    variant="normal"
                    type="number"
                    disabled={
                      !gdtSettings?.isCleanupJobEnabledGlobally ||
                      !cleanupEnabled
                    }
                    {...register('deleteAfterMonths', {
                      ...requiredRule(),
                      ...positiveRule(),
                      ...minRule(1),
                      ...maxRule(1000),
                      valueAsNumber: true,
                    })}
                    errorText={formState.errors.deleteAfterMonths?.message}
                  />
                </Field>
              </FieldBlock>
            </div>
            <FieldBlock title={i18next.t('visit_creating_settings')}>
              <Field
                title={i18next.t('default_visit_type')}
                className={styles.field}
              >
                <Controller
                  control={control}
                  name={'defaultVisitTypeId'}
                  render={({ field: { onChange, value } }) => (
                    <div className={styles.input}>
                      <DropDownInput
                        useIdFunctionAsValue={true}
                        options={translatedVisitTypes}
                        value={value}
                        idFunction={'id'}
                        getOptionLabel={'label'}
                        required={true}
                        onValueChanged={(value) => {
                          onChange(value?.id);
                          setVisitLength(value);
                        }}
                        errorText={formState.errors.defaultVisitTypeId?.message}
                      />
                    </div>
                  )}
                />
              </Field>
              <Field title={i18next.t('visit_length')} className={styles.field}>
                <Input
                  variant="normal"
                  type="number"
                  step="any"
                  {...register('visitLengthInHours', {
                    ...requiredRule(),
                    ...minRule(0.1),
                    ...maxRule(100),
                    valueAsNumber: true,
                  })}
                  errorText={formState.errors.visitLengthInHours?.message}
                />
              </Field>
              <div className={styles.searchIntervalTitle}>
                {i18next.t('case_search_interval')}
              </div>
              <div className={styles.row}>
                <Field
                  title={i18next.t('case_search_days_backward')}
                  className={styles.intervalField}
                >
                  <Input
                    variant="normal"
                    type="number"
                    {...register('caseSearchBackwardDaysCount', {
                      ...requiredRule(),
                      ...positiveRule(),
                      ...maxRule(1000),
                      valueAsNumber: true,
                    })}
                    errorText={
                      formState.errors.caseSearchBackwardDaysCount?.message
                    }
                  />
                </Field>
                <Field
                  title={i18next.t('case_search_days_forward')}
                  className={clsx(styles.right, styles.intervalField)}
                >
                  <Input
                    variant="normal"
                    type="number"
                    {...register('caseSearchForwardDaysCount', {
                      ...requiredRule(),
                      ...positiveRule(),
                      ...maxRule(1000),
                      valueAsNumber: true,
                    })}
                    errorText={
                      formState.errors.caseSearchForwardDaysCount?.message
                    }
                  />
                </Field>
              </div>
            </FieldBlock>
          </div>
        </div>
      </form>
    </Loading>
  );
});
